import React from 'react';
import type { DealDocumentRequirement } from '../data/api';

/**
 * What a deal still owes before it can be closed, per party.
 *
 * The list is computed server-side and rendered as given. The rules are
 * conditional — an individual owes an ID copy where a company owes a CIPC
 * document, a VAT-registered party owes a certificate, an SCP deal always
 * requires a GLN certificate — and re-deriving them here would be a second
 * place for them to drift from the gate that actually enforces them.
 *
 * Satisfied requirements are shown, not hidden: the operator needs to see the
 * whole checklist to know the deal is genuinely complete rather than merely
 * showing nothing outstanding.
 */
export const DealDocumentChecklist: React.FC<{
  requirements: DealDocumentRequirement[];
  canComplete: boolean;
  /** The deal is already closed: the banner reports, it does not gate. */
  isCompleted?: boolean;
  /** The filed document behind a satisfied row, if there is one on this deal. */
  linkFor?: (requirement: DealDocumentRequirement) => { href?: string; dealDocumentId?: string } | null;
  /** Which document's cost lines are open (so its button reads "Close"). */
  costsOpenId?: string;
  onToggleCosts?: (dealDocumentId: string) => void;
  /** Rendered under the list, e.g. the cost-lines panel of the open document. */
  below?: React.ReactNode;
}> = ({ requirements, canComplete, isCompleted = false, linkFor, costsOpenId = '', onToggleCosts, below }) => {
  if (!requirements.length) {
    return (
      <p style={{ fontSize: 14, opacity: 0.7 }}>
        No documents are required for this deal yet. Requirements appear once
        the seller and purchaser details say what kind of party each one is.
      </p>
    );
  }

  // Deal-level rows carry no party: removal certificates, programme-forced
  // documents and required capture fields. They were outstanding all along;
  // they must be listed, or the count above disagrees with the rows below.
  const parties: Array<{ key: 'SELLER' | 'PURCHASER' | ''; label: string }> = [
    { key: 'SELLER', label: 'Seller' },
    { key: 'PURCHASER', label: 'Purchaser' },
    { key: '', label: 'Deal' },
  ];

  const outstanding = requirements.filter((r) => !r.satisfied && !r.waived).length;
  const waived = requirements.filter((r) => !r.satisfied && r.waived).length;

  return (
    <div>
      <div style={{ marginBottom: 12, fontSize: 14 }}>
        {isCompleted ? (
          <span style={{ color: waived || !outstanding ? '#3a4a52' : '#6b1010' }}>
            {waived
              ? `This deal is closed. ${waived} requirement${waived === 1 ? ' was' : 's were'} waived under an approved override.`
              : outstanding
                ? `This deal is closed with ${outstanding} requirement${outstanding === 1 ? '' : 's'} still outstanding.`
                : 'This deal is closed with every requirement met.'}
          </span>
        ) : canComplete ? (
          <span style={{ color: '#106b38' }}>
            {waived
              ? `All outstanding requirements are waived under an approved override (${waived}) — this deal can be closed.`
              : 'All required documents are present — this deal can be closed.'}
          </span>
        ) : (
          <span style={{ color: '#6b1010' }}>
            {outstanding} requirement{outstanding === 1 ? '' : 's'} outstanding.
            This deal cannot be closed until they are met.
          </span>
        )}
      </div>

      {parties.map(({ key, label }) => {
        const forParty = requirements.filter((r) => r.party === key);
        if (!forParty.length) return null;
        return (
          <div key={key} style={{ marginBottom: 14 }}>
            <div style={{
              fontSize: 12,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              opacity: 0.65,
              marginBottom: 6,
            }}>
              {label}
            </div>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {forParty.map((requirement) => (
                <li
                  key={`${requirement.party}-${requirement.code}`}
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 8,
                    padding: '4px 0',
                    fontSize: 14,
                  }}
                >
                  <span
                    aria-hidden
                    style={{ color: requirement.satisfied ? '#2f6f4e' : '#b06a00' }}
                  >
                    {requirement.satisfied ? '✓' : requirement.waived ? '⌀' : '○'}
                  </span>
                  <span>
                    <span style={{
                      textDecoration: requirement.satisfied ? 'line-through' : 'none',
                      opacity: requirement.satisfied ? 0.65 : 1,
                    }}>
                      {requirement.label}
                    </span>
                    {requirement.waived && !requirement.satisfied && (
                      <span
                        title="Outstanding, but covered by an approved close-out override"
                        style={{
                          marginLeft: 8, fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                          letterSpacing: '0.06em', color: '#7b4a18', background: '#fff7ef',
                          border: '1px solid #ecd1b9', borderRadius: 999, padding: '1px 7px',
                        }}
                      >
                        waived
                      </span>
                    )}
                    {/* The reason matters: "why am I being asked for this?" is
                        the first question, and the answer is a fact about the
                        party the operator can go and correct. */}
                    <span style={{ opacity: 0.6, marginLeft: 8, fontSize: 13 }}>
                      — {requirement.reason}
                    </span>
                    {requirement.satisfied && requirement.satisfied_by === 'entity' && (
                      <span style={{ marginLeft: 8, fontSize: 12, color: '#106b38' }}>on the entity’s file</span>
                    )}
                    {(() => {
                      const link = requirement.satisfied && linkFor ? linkFor(requirement) : null;
                      if (!link) return null;
                      return (
                        <span style={{ marginLeft: 10, display: 'inline-flex', gap: 8 }}>
                          {link.href && (
                            <a href={link.href} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: '#1e6bb8', textDecoration: 'underline' }}>
                              View
                            </a>
                          )}
                          {link.dealDocumentId && onToggleCosts && (
                            <button
                              type="button"
                              onClick={() => onToggleCosts(link.dealDocumentId!)}
                              style={{ fontSize: 13, color: '#1e6bb8', background: 'none', border: 'none', padding: 0, cursor: 'pointer', textDecoration: 'underline' }}
                            >
                              {costsOpenId === link.dealDocumentId ? 'Close costs' : 'Add costs'}
                            </button>
                          )}
                        </span>
                      );
                    })()}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
      {below}
    </div>
  );
};

/** Group requirements by party, for callers that want their own layout. */
export function groupRequirements(
  requirements: DealDocumentRequirement[],
): Record<string, DealDocumentRequirement[]> {
  return requirements.reduce((acc, requirement) => {
    (acc[requirement.party] ||= []).push(requirement);
    return acc;
  }, {} as Record<string, DealDocumentRequirement[]>);
}
